<script setup lang="ts">
import { useInvoiceStore } from '@/entityes/invoice/invoice.store'
import useInvoiceRemoteAdminRequest from '@/06-Entities/AdminInvoiceRemote/api/invoice_remote.admin.request'
import { computed, ref } from 'vue'
import { toTypedSchema } from '@vee-validate/yup'
import * as yup from 'yup'
import FormItem from '@/07-Shared/ui/FormItem.vue'
import { useInvoiceRepository } from '@/entityes/invoice/invoice.repository'
import { ElNotification } from 'element-plus'
import { useForm, useFormValues } from 'vee-validate'
import { IInvoice } from '@/entityes/invoice/invoice.type'
import { useAsyncInvoiceReload } from '@/entityes/invoice/useAsyncInvoiceReload'
import { SuccessFilled, Refresh, CircleCloseFilled, Loading, DocumentAdd, Histogram } from '@element-plus/icons-vue'
import { useCfrRepository } from '@/entityes/invoice/cfr/cfr.repository'
import { useRouter } from 'vue-router'
import { useYears } from '@/07-Shared/model/useYears'

const invoiceStore = useInvoiceStore()
const router = useRouter()
const { recalcDeclaration, recalcCfr, assignInvoice, toggleBlockInvoice, rerender, updateInvoiceYear } =
  useInvoiceRemoteAdminRequest()
const { getInvoice } = useInvoiceRepository()
const asyncInvoice = computed(() => !!invoiceStore.invoice && invoiceStore.invoice.status.async)
const blocked = computed(() => !!invoiceStore.invoice && invoiceStore.invoice.status.blocked)
const declarationId = computed(() => !!invoiceStore.invoice && invoiceStore.invoice.goods.Declaration)
const cfrId = computed(() => !!invoiceStore.invoice && invoiceStore.invoice.goods.Cfr)
const schema = toTypedSchema(
  yup.object({
    email: yup.string().required().email(),
  })
)
const { handleSubmit } = useForm({
  validationSchema: schema,
})
const formValues = useFormValues()
const isLoading = ref(false)
const dropTM = ref(false)
const yearToCalc = ref(invoiceStore.invoice?.year_to_calc)
const { initAsync } = useAsyncInvoiceReload()
const { createXmlCfr } = useCfrRepository()

function recalculateDeclaration() {
  isLoading.value = true
  recalcDeclaration(declarationId.value as number, { flush: dropTM.value })
    .then(() => {
      getInvoice(invoiceStore.invoice?.id as number).then((res) => {
        initAsync(res)

        ElNotification({
          title: 'Успешно',
          message: `отправлен на пересчет декларация ${declarationId.value}`,
          type: 'success',
        })
      })
    })
    .catch((err) => {
      ElNotification({
        title: 'Ошибка',
        message: `при пересчете. Попробуйте позже. ${err}`,
        type: 'error',
      })
    })
    .finally(() => (isLoading.value = false))
}

function recalculateCfr() {
  isLoading.value = true
  recalcCfr(cfrId.value as number)
    .then(() => {
      getInvoice(invoiceStore.invoice?.id as number).then(() => {
        ElNotification({
          title: 'Успешно',
          message: `отправлен на пересчет заказ ${invoiceStore.invoice?.id}`,
          type: 'success',
        })
        initAsync(invoiceStore.invoice as IInvoice)
      })
    })
    .catch((err) => {
      ElNotification({
        title: 'Ошибка',
        message: `при пересчете. Попробуйте позже. ${err}`,
        type: 'error',
      })
    })
    .finally(() => (isLoading.value = false))
}
function rerenderDeclarationRes() {
  isLoading.value = true
  rerender(declarationId.value as number)
    .then(() => {
      getInvoice(invoiceStore.invoice?.id as number).then(() => {
        ElNotification({
          title: 'Успешно',
          message: `начат пересчет итоговых документов декларации`,
          type: 'success',
        })
        initAsync(invoiceStore.invoice)
      })
    })
    .finally(() => (isLoading.value = false))
}
function updateBlock(val: boolean) {
  isLoading.value = true
  toggleBlockInvoice(invoiceStore.invoice.id, { blocked: val }).then((res) => {
    getInvoice(res.id).finally(() => (isLoading.value = false))
  })
}
function createCfrFile() {
  isLoading.value = true
  if (!!invoiceStore?.invoice && Object.keys(invoiceStore?.invoice?.goods).includes('Cfr')) {
    createXmlCfr(invoiceStore?.invoice?.goods?.Cfr as string | number)
      .then(() => {
        ElNotification({
          title: 'Успешно',
          message: 'отправлен запрос на создание файла xml ОДС',
          type: 'success',
        })
        getInvoice(invoiceStore.invoice.id).then((res) => {
          initAsync(res)
        })
      })
      .finally(() => (isLoading.value = false))
      .catch((err) => {
        ElNotification({
          title: 'Ошибка',
          message: `отправки запроса на создание файла xml ОДС | ${err}`,
          type: 'error',
        })
      })
  }
}
function moveToCalcIncome() {
  router.push({ name: 'calculated_incomes' })
}
const assignInvoiceToUser = handleSubmit((form) => {
  isLoading.value = true
  if (!!invoiceStore.invoice) {
    assignInvoice(invoiceStore.invoice?.id as string | number, form)
      .then((res: IInvoice) => {
        ElNotification({
          title: 'Успешно',
          message: `заказ ${res.id} привязан к пользователю ${res.email}`,
          type: 'success',
        })
        getInvoice(res.id)
      })
      .catch((err) => {
        ElNotification({
          title: 'Ошибка',
          message: `во время привзяки заказа к пользователю ${formValues.value.email}. Попробуйте позже. ${err}`,
          type: 'error',
        })
      })
      .finally(() => (isLoading.value = false))
  }
})
function setYearToCalc(year: number) {
  isLoading.value = true
  updateInvoiceYear(invoiceStore.invoice?.id as number, { year })
    .then((res) => {
      console.log(res)
      ElNotification({
        title: 'Успешно',
        message: `В инвойсе ${invoiceStore.invoice?.id} расчетный год изменен на ${res.year_to_calc}`,
        type: 'success',
      })
    })
    .catch((err) => {
      ElNotification({
        title: 'Ошибка',
        message: `изменения расчетного года в инвойсе ${invoiceStore.invoice?.id} | ${err}`,
        type: 'success',
      })
    })
    .finally(() => (isLoading.value = false))
}
</script>

<template>
  <div>
    <div class="flex">
      <div class="flex mr-6">
        <div>
          <h4 class="font-weight--extra font-color--deep-gray mb-2">Привязать</h4>
          <div class="flex flex-nowrap">
            <div style="width: 200px" class="mr-2">
              <el-form @submit.prevent="assignInvoice" :validation-schema="schema">
                <FormItem name="email" v-slot="{ value, onBlur, onInput }" :label="''">
                  <el-input
                    :model-value="value"
                    type="text"
                    name="email"
                    size="small"
                    @blur="onBlur"
                    @input="onInput"
                    placeholder="example@ya.ru"
                  />
                </FormItem>
              </el-form>
            </div>
            <div>
              <el-button
                size="small"
                type="primary"
                :loading="isLoading"
                :disabled="isLoading || asyncInvoice"
                @click="assignInvoiceToUser"
                >ok</el-button
              >
            </div>
          </div>
        </div>
      </div>
      <div class="mr-6">
        <h4 class="font-weight--extra font-color--deep-gray mb-2">Изменить год</h4>
        <el-select
          v-loading="isLoading"
          size="small"
          v-model="yearToCalc"
          placeholder="Выберите год"
          @change="setYearToCalc"
        >
          <el-option v-for="year in useYears" :key="year" :label="year" :value="year" />
        </el-select>
      </div>
      <div class="mr-6">
        <h4 class="font-weight--extra mb-2 font-color--deep-gray">ОДС</h4>
        <div v-if="!asyncInvoice">
          <el-tooltip class="box-item" effect="dark" content="Пересчитать ОДС" placement="top">
            <el-button
              size="small"
              :loading="isLoading"
              type="primary"
              @click="recalculateCfr"
              :disabled="isLoading || asyncInvoice"
              :icon="Refresh"
            >
              Пересчитать
            </el-button>
          </el-tooltip>
        </div>
        <div class="flex items-center" v-else>
          <el-icon color="#FF9A9D" class="is-loading mr-2" v-if="asyncInvoice"><Loading /></el-icon>
          <p class="font-color--red">расчет</p>
        </div>
      </div>
      <div class="mr-6">
        <h4 class="font-weight--extra mb-2 font-color--deep-gray">Перерисовать</h4>

        <el-tooltip effect="dark" content="Перерисовать декларацию" placement="top">
          <el-button
            size="small"
            :loading="isLoading"
            type="info"
            :icon="Refresh"
            @click="rerenderDeclarationRes"
            :disabled="isLoading || asyncInvoice"
          >
            Перерисовать
          </el-button>
        </el-tooltip>
      </div>
      <div class="mr-6">
        <h4 class="font-weight--extra mb-1 font-color--deep-gray">Декларация</h4>
        <div v-if="!asyncInvoice" class="flex items-center">
          <div class="mr-6">
            <el-tooltip class="box-item" effect="dark" content="Пересчитать Декларацию" placement="top">
              <el-button
                size="small"
                :loading="isLoading"
                type="primary"
                @click="recalculateDeclaration"
                :disabled="isLoading || asyncInvoice"
                :icon="Refresh"
              >
                Пересчитать
              </el-button>
            </el-tooltip>
          </div>
          <div>
            <el-checkbox v-model="dropTM" label="Удалить трансферы и миссинги"></el-checkbox>
          </div>
        </div>
        <div class="flex items-center" v-else>
          <el-icon color="#FF9A9D" class="is-loading mr-2" v-if="asyncInvoice"><Loading /></el-icon>
          <p class="font-color--red">расчет</p>
        </div>
      </div>
    </div>
    <div class="el-divider-purple my-2"></div>
    <div class="flex">
      <div class="mr-6">
        <h4 class="font-weight--extra mb-2 font-color--deep-gray">
          Блокировка:
          <span class="font-weight--extra" :class="blocked ? 'font-color--red' : 'font-color--green'">{{
            blocked ? 'да' : 'нет'
          }}</span>
        </h4>
        <el-tooltip
          class="box-item"
          effect="dark"
          :content="`${blocked ? 'Открыть' : `Закрыть`} скачивание документов`"
          placement="top"
        >
          <el-button
            size="small"
            :loading="isLoading"
            :type="blocked ? 'success' : 'danger'"
            @click="updateBlock(!blocked)"
            :disabled="isLoading || asyncInvoice"
            :icon="blocked ? SuccessFilled : CircleCloseFilled"
          >
            {{ blocked ? 'Разблокировать' : 'Заблокировать' }}
          </el-button>
        </el-tooltip>
      </div>
      <div class="mr-6">
        <h4 class="mb-2">Файл xml ОДС</h4>
        <el-button @click="createCfrFile" :loading="isLoading" :icon="DocumentAdd" size="small" type="primary"
          >Создать</el-button
        >
      </div>
      <div>
        <h4 class="mb-2">Торговые операции</h4>
        <el-button @click="moveToCalcIncome" :loading="isLoading" :icon="Histogram" size="small" type="primary">
          ТО
        </el-button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="sass"></style>
